/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import HybridLink from '../../../components/hybrid-link'
import {BrandLogo, LockIcon} from '../../../components/icons'
import useCustomer from '../../../commerce-api/hooks/useCustomer'
import {ACCOUNT_HREF, HYBRID_ACCOUNT, HYBRID_HOME} from '../../../constants'
import textStyles from '../../../theme/foundations/textStyles'
import PropTypes from 'prop-types'

const CheckoutHeader = ({isCheckoutConfirmation = false}) => {
    const intl = useIntl()
    const customer = useCustomer()
    const isRegistered = customer.isRegistered
    const brandText = intl.formatMessage({defaultMessage: 'STOKKE Logo', id: 'global.brand_logo'})
    const lockIconText = intl.formatMessage({
        defaultMessage: 'Lock Icon',
        id: 'checkout-header.label.lock_icon'
    })
    const styles = useMultiStyleConfig('Header', {variant: 'checkout'})
    const showAccountLink = isRegistered && isCheckoutConfirmation

    return (
        <Box bg="white" as="header">
            <Box {...styles.mainContainer}>
                <Flex width="100%" height="88px" align="center" justify="space-between">
                    <HybridLink
                        page={HYBRID_HOME}
                        href="/home"
                        title="Back to homepage"
                        variant="image"
                    >
                        <BrandLogo
                            width={{base: '56px'}}
                            height={{base: '56px'}}
                            role="img"
                            aria-label={brandText}
                        />
                    </HybridLink>

                    <Flex direction="row">
                        {showAccountLink ? (
                            <HybridLink
                                {...textStyles['Regular Regular Medium']}
                                page={HYBRID_ACCOUNT}
                                to={ACCOUNT_HREF}
                            >
                                <FormattedMessage
                                    defaultMessage="My Stokke"
                                    id="global.my_account"
                                />
                            </HybridLink>
                        ) : (
                            <>
                                <Text {...textStyles['Semi Bold Small']} mt="2px">
                                    <FormattedMessage
                                        defaultMessage="Secure Checkout"
                                        id="global.secure_checkout"
                                    />
                                </Text>
                                <LockIcon
                                    width={{base: '16px'}}
                                    height={{base: '16px'}}
                                    ml="6px"
                                    role="img"
                                    aria-label={lockIconText}
                                    aria-hidden="true"
                                    data-testid="lock-logo"
                                />
                            </>
                        )}
                    </Flex>
                </Flex>
            </Box>
        </Box>
    )
}

CheckoutHeader.propTypes = {
    isCheckoutConfirmation: PropTypes.bool
}

export default CheckoutHeader
